/**
 * The structured interpretation of a brief/JD — the legible fact sheet a
 * recruiter reviews ("Confira o que entendemos do briefing"): role +
 * seniority/level, experience window, location, sector, and must-haves vs.
 * differentials, all straight off an already-parsed `StructuredQuery` (no
 * fetch, no spend). Shared by the explore flow's pre-flight review, the
 * project creation review, and the project page's JD card.
 */
import type { StructuredQuery } from "@shared/labs/sourcing";
import { Check, MapPin } from "lucide-react";
import type React from "react";
import { effectiveSearchRegions } from "./lib";

/** Human label for the stated experience window. The judge treats a candidate far
 *  beyond `max` as over-qualified, so this is a target RANGE, not a floor. */
export function yearsLabel(min: number | undefined, max: number | undefined): string | null {
  if (min != null && max != null) return `${min}–${max} anos`;
  if (min != null) return `a partir de ${min} anos`;
  if (max != null) return `até ${max} anos`;
  return null;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

/** A titled criteria block (must-haves / differentials) with its semantic hint + chips. */
function Criteria({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xs font-semibold text-foreground">{title}</span>
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

export function QueryInterpretation({
  query,
  eyebrow,
  heading,
}: {
  query: StructuredQuery;
  /** Optional mono-caps eyebrow above the heading (e.g. "Revise antes de começar"). */
  eyebrow?: string;
  heading?: string;
}) {
  const f = query.filters;
  // Seniority label + pinned leveling-model level (e.g. "PLENO · L3"), each optional.
  const levelChips = [f.seniority, query.targetLevelLabel].filter(Boolean) as string[];
  const years = yearsLabel(f.minYearsExperience, f.maxYearsExperience);
  // Effective search regions — the countries each arm targets. Always present (≥ Brazil, the safety
  // net), so the recruiter sees exactly where the search runs. Each branch is one arm's OR-set.
  const regions = effectiveSearchRegions(query)
    .map((b) => b.join(" / "))
    .join(" · ");
  // Must-haves = the LLM's human-readable required skills (kept minimal; AND-combined,
  // hard gate). `requiredConcepts` are ontology ids — sparse + unlabeled client-side —
  // so they're intentionally not surfaced here.
  const mustHaves = f.requiredSkills;
  const niceToHaves = f.niceToHaves ?? [];

  return (
    <>
      {eyebrow && (
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </span>
      )}
      {heading && <h3 className="mt-1 text-base font-semibold tracking-tight">{heading}</h3>}

      {/* Structured fact sheet — the parsed interpretation, field by field. */}
      <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Fact label="Cargo">
          <span className="font-medium text-foreground">{f.title ?? "a vaga"}</span>
          {levelChips.length > 0 && (
            <span className="ml-2 inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-primary">
              {levelChips.join(" · ")}
            </span>
          )}
        </Fact>
        {years && (
          <Fact label="Experiência">
            <span className="font-medium text-foreground">{years}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
              faixa-alvo, não um mínimo — muito acima conta como sobrequalificado
            </span>
          </Fact>
        )}
        <Fact label="Regiões de busca">
          <span className="inline-flex items-center gap-1 font-medium text-foreground">
            <MapPin size={13} className="text-muted-foreground" />
            {regions}
          </span>
        </Fact>
        {f.industry && (
          <Fact label="Setor">
            <span className="font-medium text-foreground">{f.industry}</span>
          </Fact>
        )}
      </dl>

      {/* Must-haves — hard gate. */}
      {mustHaves.length > 0 && (
        <Criteria title="Obrigatórios" hint="todo perfil precisa atender — quem não tem é excluído">
          {mustHaves.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 rounded-full border border-foreground/25 bg-card px-2.5 py-1 text-xs font-medium text-foreground"
            >
              <Check size={12} className="text-muted-foreground" />
              {s}
            </span>
          ))}
        </Criteria>
      )}

      {/* Nice-to-haves — soft preferences, never exclude. */}
      {niceToHaves.length > 0 && (
        <Criteria title="Diferenciais" hint="melhoram o ranqueamento, nunca excluem">
          {niceToHaves.map((s) => (
            <span
              key={s}
              className="rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </Criteria>
      )}
    </>
  );
}
